import { ColorlibConnector, ColorlibStepIcon } from "@components/Stepper";
import { BUTTON_PRIMARY_ICON_FONT_SIZE } from "@constants/button";
import Routes, { ROUTING_SIMULATOR } from "@constants/routing";
import { ClientContext } from "@context/Client/ClientContext";
import { useTimePeriods } from "@hooks";
import LayoutSelectors from "@layouts/LayoutSelectors";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import SaveIcon from "@mui/icons-material/Save";
import SendIcon from "@mui/icons-material/Send";
import {
  Box,
  Card,
  CardActions,
  CardContent,
  IconButton,
  Stack,
  Step,
  StepLabel,
  Stepper,
  Tooltip,
  Typography
} from "@mui/material";
import {
  useClientModelInputs,
  useClientModelVariables,
  useModelCreateScenario
} from "@services/api";
import {
  CustomClientModelVariable,
  ModelCreateScenario
} from "@services/api/types";
import { useQueryClient } from "@tanstack/react-query";
import {
  getExternalEffectsValues,
  getExternalVarsPredefinedValues,
  IVariableValues
} from "@utils/external_effects";
import { DateTime } from "luxon";
import { useContext, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate } from "react-router-dom";
import StepExternalVars, {
  StepsExternalVarsSkeleton
} from "./components/Steps/StepExternalVars";
import StepMediaVars from "./components/Steps/StepMediaVars";
import StepSimulation from "./components/Steps/StepSimulation";

const ViewNewScenario = () => {
  const { t } = useTranslation();
  const { currentClient } = useContext(ClientContext);
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [activeStep, setActiveStep] = useState(0);
  const timePeriodIds = useTimePeriods(currentClient.model.aggregation_type);
  const [timePeriods, setTimePeriods] = useState<string[]>([...timePeriodIds]);
  const [extVariableValues, setExtVariableValues] = useState<IVariableValues>(
    {}
  );
  const [mediaVariableValues, setMediaVariableValues] =
    useState<IVariableValues>({});
  const [currentMediaModelVars, setCurrentMediaModelVars] = useState<
    CustomClientModelVariable[]
  >([]);

  const [newScenario, setNewScenario] = useState<ModelCreateScenario>({
    scenario: {
      id: undefined,
      model_id: Number(currentClient.model.id),
      name: "",
      description: "",
      time_period: "",
      year: DateTime.now().year,
      media_investment: 0,
      predicted_inflow: 0,
      target_inflow: 0,
      is_favorite: false,
      forecast_selected: false,
      inputs: [],
      outputs: []
    },
    inputs: [],
    outputs: []
  });

  const { data: modelVars, isFetching: isLoadingMediaVars } =
    useClientModelVariables(currentClient.model.id, "media");

  const { data: modelExtVars, isFetching: isLoadingExtVars } =
    useClientModelVariables(currentClient.model.id, "external");

  const modelVarsNames = useMemo(
    () => (modelExtVars || []).map(({ name }) => name),
    [modelExtVars]
  );
  const {
    data: extVarsPredefinedValues,
    isFetching: isLoadingExtVarsPredValues
  } = useClientModelInputs(
    currentClient.model.id.toString(),
    modelVarsNames,
    timePeriodIds
  );

  const mutationCreateScenario = useModelCreateScenario();

  const predefinedExternalValues = useMemo(
    () => getExternalVarsPredefinedValues(extVarsPredefinedValues || []),
    [extVarsPredefinedValues]
  );

  const handleNext = () => {
    setActiveStep((prevActiveStep) => prevActiveStep + 1);
  };

  const handleSave = () => {
    if (!canSaveScenarioSimulation) {
      return;
    }
    mutationCreateScenario.mutate(
      { data: newScenario },
      {
        onSuccess: async () => {
          await queryClient.invalidateQueries({
            queryKey: ["model", currentClient.model.id.toString(), "scenarios"]
          });
          await queryClient.refetchQueries({
            queryKey: ["model", currentClient.model.id.toString(), "scenarios"]
          });
          navigate(Routes.SIMULATOR);
        }
      }
    );
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  useEffect(() => {
    if (modelVars) {
      setCurrentMediaModelVars(
        modelVars.map((modelVar) => ({ ...modelVar, is_custom: false }))
      );
    }
  }, [setCurrentMediaModelVars, modelVars]);

  const canSaveScenarioSimulation = useMemo(
    () =>
      (newScenario?.inputs || []).length &&
      (newScenario?.outputs || []).length &&
      newScenario?.scenario.name.length,
    [newScenario]
  );

  const isLoading = useMemo(
    () => isLoadingMediaVars || isLoadingExtVars || isLoadingExtVarsPredValues,
    [isLoadingExtVars, isLoadingMediaVars, isLoadingExtVarsPredValues]
  );

  useEffect(() => {
    if (isLoading) {
      return;
    }

    const values = getExternalEffectsValues(
      timePeriods,
      extVariableValues,
      predefinedExternalValues,
      modelExtVars
    );

    setExtVariableValues(values);
  }, [
    setExtVariableValues,
    isLoading,
    modelVars,
    predefinedExternalValues,
    timePeriods,
    modelExtVars
  ]);

  useEffect(() => {
    if (isLoading) {
      return;
    }

    const values: {
      [key: string]: { [key: number]: number };
    } = {};

    timePeriods.forEach((timePeriod) => {
      values[timePeriod] = {};
      currentMediaModelVars.forEach((modelVar) => {
        let modelPeriodValue = 0;
        if (
          timePeriod in mediaVariableValues &&
          modelVar.id in mediaVariableValues[timePeriod]
        ) {
          modelPeriodValue = mediaVariableValues[timePeriod][modelVar.id];
        }
        values[timePeriod][modelVar.id] = modelPeriodValue;
      });
    });
    setMediaVariableValues(values);
  }, [setMediaVariableValues, currentMediaModelVars, isLoading, timePeriods]);

  const steps = [
    t("simulator.new_scenario.external_vars"),
    t("simulator.new_scenario.media_vars", {
      currency: currentClient.client.currency
    }),
    t("simulator.new_scenario.simulation")
  ];

  return (
    <LayoutSelectors disabled={true}>
      <Box className="w-full pt-4 pl-8 pr-8 mb-6">
        <Box className="w-full flex pb-4">
          <Typography variant="h6" component="div" sx={{ fontSize: 16 }}>
            {t("navbar.tabs.simulator")}
          </Typography>
          <ArrowForwardIosIcon className="pt-1" />
          <Link to={ROUTING_SIMULATOR} key="scenario-to-simualtor-link">
            <Typography variant="h6" component="div" sx={{ fontSize: 16 }}>
              {t("simulator.pages.scenarios")}
            </Typography>
          </Link>
          <ArrowForwardIosIcon className="pt-1" />
          <Typography variant="h6" component="div" sx={{ fontSize: 16 }}>
            {t("simulator.scenarios.new_scenario")}
          </Typography>
        </Box>
        {isLoading && (
          <StepsExternalVarsSkeleton
            columnsCount={timePeriods.length + 1}
            key={`${currentClient.model.id}-steps-external-vars-skeleton`}
          />
        )}

        {!isLoading && (
          <Card className="w-full">
            <CardContent>
              <Stack className="w-full" spacing={4}>
                <Stepper
                  alternativeLabel
                  activeStep={activeStep}
                  connector={<ColorlibConnector />}
                >
                  {steps.map((label) => (
                    <Step key={label}>
                      <StepLabel StepIconComponent={ColorlibStepIcon}>
                        {label}
                      </StepLabel>
                    </Step>
                  ))}
                </Stepper>
              </Stack>
              {activeStep === 0 && (
                <StepExternalVars
                  timePeriods={timePeriods}
                  setTimePeriods={setTimePeriods}
                  variableValues={extVariableValues}
                  setVariableValues={setExtVariableValues}
                />
              )}
              {activeStep === 1 && (
                <StepMediaVars
                  timePeriods={timePeriods}
                  variableValues={mediaVariableValues}
                  setVariableValues={setMediaVariableValues}
                  mediaVars={currentMediaModelVars}
                  setMediaVars={setCurrentMediaModelVars}
                />
              )}
              {activeStep === 2 && (
                <StepSimulation
                  timePeriods={timePeriods}
                  externalValues={extVariableValues}
                  mediaValues={mediaVariableValues}
                  mediaVars={currentMediaModelVars}
                  extVars={modelExtVars || []}
                  setNewScenario={setNewScenario}
                />
              )}
            </CardContent>
            <CardActions>
              <Box className="w-1/2 text-left ml-6 mb-4">
                <Tooltip title={t("common.goBack")}>
                  <IconButton
                    color="info"
                    onClick={handleBack}
                    disabled={activeStep === 0}
                  >
                    <ArrowBackIosNewIcon
                      sx={{
                        backgroundColor: "white",
                        fontSize: BUTTON_PRIMARY_ICON_FONT_SIZE
                      }}
                    />
                  </IconButton>
                </Tooltip>
              </Box>
              <Box className="w-1/2 text-right mr-6 mb-4">
                {activeStep === 0 && (
                  <Tooltip title={t("common.next")}>
                    <IconButton color="info" onClick={handleNext}>
                      <ArrowForwardIosIcon
                        sx={{
                          backgroundColor: "white",
                          fontSize: BUTTON_PRIMARY_ICON_FONT_SIZE
                        }}
                      />
                    </IconButton>
                  </Tooltip>
                )}
                {activeStep === 1 && (
                  <Tooltip title={t("common.simulate")}>
                    <IconButton color="info" onClick={handleNext}>
                      <SendIcon
                        sx={{
                          backgroundColor: "white",
                          fontSize: BUTTON_PRIMARY_ICON_FONT_SIZE
                        }}
                      />
                    </IconButton>
                  </Tooltip>
                )}
                {activeStep === 2 && (
                  <Tooltip title={t("common.save")}>
                    <IconButton
                      color="success"
                      onClick={handleSave}
                      disabled={!canSaveScenarioSimulation}
                    >
                      <SaveIcon
                        sx={{
                          backgroundColor: "white",
                          fontSize: BUTTON_PRIMARY_ICON_FONT_SIZE
                        }}
                      />
                    </IconButton>
                  </Tooltip>
                )}
              </Box>
            </CardActions>
          </Card>
        )}
      </Box>
    </LayoutSelectors>
  );
};

export default ViewNewScenario;
